import { Availability, AssociatedDay, DateRange } from '@core/models';

export interface AvailabilityScheduleContext {
	availabilities: Availability[];
	now: Date;
}

export type AvailabilityScheduleRule = <T extends AvailabilityScheduleContext>(context: T) => T;

export const availabilityOutsideCurrentScheduleRule: AvailabilityScheduleRule = <
	T extends AvailabilityScheduleContext
>(context: T): T => ({
	...context,
	availabilities: context.availabilities.filter(
		availability => !isAvailabilityWithinSchedule(availability, context.now)
	),
});

export function isAvailabilityWithinSchedule(availability: Availability, now: Date): boolean {
	return availability.porRangoFecha
		? dateInRange(availability.rangoFechaAsociado ?? {} as DateRange, now)
		: inDay(availability.diasAsociados ?? [], now);
}

export function getUnavailableProductIds(
	availabilities: Availability[] | null | undefined,
	now = new Date()
): string[] {
	const unavailableAvailabilities = availabilityOutsideCurrentScheduleRule({
		availabilities: [...(availabilities ?? [])],
		now,
	}).availabilities;

	return [...new Set(
		unavailableAvailabilities.flatMap(availability => availability.productosAsociados ?? [])
	)];
}

export function isProductAvailable(
	menuItemId: string,
	availabilityList: Availability[] | null | undefined,
	now = new Date()
): boolean {
	return !getUnavailableProductIds(availabilityList, now).includes(menuItemId);
}

export function inDay(days: AssociatedDay[], now = new Date()): boolean {
	if (days.length === 0) return false;

	const currentDay = [
		'sunday',
		'monday',
		'tuesday',
		'wednesday',
		'thursday',
		'friday',
		'saturday',
	][now.getDay()];
	const associatedDay = days.find(day =>
		normalizeDayName(day.nombreDia ?? '') === currentDay
	);

	if (!associatedDay?.habilitado) return false;
	if (!associatedDay.horaInicio || !associatedDay.horaFin) return true;

	const [startHour, startMinute] = associatedDay.horaInicio.split(':').map(Number);
	const [endHour, endMinute] = associatedDay.horaFin.split(':').map(Number);
	const currentMinutes = now.getHours() * 60 + now.getMinutes();
	const startMinutes = startHour * 60 + startMinute;
	const endMinutes = endHour * 60 + endMinute;

	return startMinutes <= endMinutes
		? currentMinutes >= startMinutes && currentMinutes <= endMinutes
		: currentMinutes >= startMinutes || currentMinutes <= endMinutes;
}

export function dateInRange(range: DateRange, now = new Date()): boolean {
	const { rangoFechaInicio, rangoFechaFin, horaInicio, horaFin } = range;
	if (!rangoFechaInicio || !rangoFechaFin || !horaInicio || !horaFin) return false;

	const [startYear, startMonth, startDay] = rangoFechaInicio.split('-').map(Number);
	const [endYear, endMonth, endDay] = rangoFechaFin.split('-').map(Number);
	const startDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0);
	const endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59);
	if (now < startDate || now > endDate) return false;

	const [startHour, startMinute] = horaInicio.split(':').map(Number);
	const [endHour, endMinute] = horaFin.split(':').map(Number);
	const scheduleStart = new Date(
		now.getFullYear(), now.getMonth(), now.getDate(), startHour, startMinute, 0
	);
	const scheduleEnd = new Date(
		now.getFullYear(), now.getMonth(), now.getDate(), endHour, endMinute, 59
	);
	const currentTime = new Date(
		now.getFullYear(), now.getMonth(), now.getDate(),
		now.getHours(), now.getMinutes(), now.getSeconds()
	);

	return currentTime >= scheduleStart && currentTime <= scheduleEnd;
}

export const isDayActive = inDay;
export const isDateRangeActive = dateInRange;

function normalizeDayName(dayName: string): string {
	const normalized = dayName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
	const dayNames: Record<string, string> = {
		lunes: 'monday',
		martes: 'tuesday',
		miercoles: 'wednesday',
		jueves: 'thursday',
		viernes: 'friday',
		sabado: 'saturday',
		domingo: 'sunday',
	};

	return dayNames[normalized] ?? normalized;
}
